import { messaging, getToken, onMessage } from '../firebase/config';
import { doc, updateDoc, deleteField } from 'firebase/firestore';
import { db } from '../firebase/config';

// VAPID 키 (Firebase 콘솔 > 프로젝트 설정 > 클라우드 메시징에서 생성)
// TODO: Firebase 콘솔에서 실제 VAPID 키로 교체 필요
const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY || '';

// 타임아웃 유틸
const withTimeout = (promise, ms) => {
    return Promise.race([
        promise,
        new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), ms))
    ]);
};

// 알림 권한 요청 및 토큰 가져오기
export const requestNotificationPermission = async (userId) => {
    try {
        // 브라우저 지원 확인
        if (!('Notification' in window)) {
            console.log('이 브라우저는 알림을 지원하지 않습니다.');
            return { success: false, error: 'not_supported' };
        }

        // 권한 요청 (5초 타임아웃)
        let permission;
        try {
            permission = await withTimeout(Notification.requestPermission(), 5000);
        } catch (e) {
            console.log('알림 권한 요청 타임아웃');
            return { success: false, error: 'timeout' };
        }

        if (permission !== 'granted') {
            console.log('알림 권한이 거부되었습니다.');
            return { success: false, error: 'permission_denied' };
        }

        // Firestore에 알림 활성화 저장
        if (userId) {
            try {
                await withTimeout(updateDoc(doc(db, 'users', userId), {
                    notificationEnabled: true,
                    notificationUpdatedAt: new Date()
                }), 5000);
            } catch (e) {
                console.log('알림 설정 업데이트 실패:', e);
            }
        }

        // VAPID 키가 설정되어 있지 않으면 FCM 토큰 없이 로컬 알림만 사용
        if (!VAPID_KEY || VAPID_KEY === 'YOUR_VAPID_KEY_HERE') {
            console.log('VAPID 키가 설정되지 않아 로컬 알림만 사용합니다.');
            return { success: true, token: null, localOnly: true };
        }

        // FCM 토큰 가져오기 (10초 타임아웃)
        if (messaging) {
            try {
                const token = await withTimeout(getToken(messaging, { vapidKey: VAPID_KEY }), 10000);

                if (token && userId) {
                    await updateDoc(doc(db, 'users', userId), {
                        fcmToken: token,
                        notificationEnabled: true,
                        notificationUpdatedAt: new Date()
                    });
                    console.log('FCM 토큰 저장 완료');
                }

                return { success: true, token };
            } catch (e) {
                console.log('FCM 토큰 가져오기 실패, 로컬 알림으로 전환:', e);
                return { success: true, token: null, localOnly: true };
            }
        }

        return { success: true, token: null, localOnly: true };
    } catch (error) {
        console.error('알림 권한 요청 실패:', error);
        return { success: false, error: error.message };
    }
};

// 포그라운드 메시지 리스너 설정
// NotificationBell이 Firestore 구독으로 인앱 토스트를 이미 표시하므로,
// FCM 포그라운드에서는 브라우저 알림을 표시하지 않음 (중복 방지)
export const setupForegroundListener = (callback) => {
    if (!messaging) return null;

    return onMessage(messaging, (payload) => {
        console.log('포그라운드 메시지 수신:', payload);

        // 커스텀 콜백 실행 (데이터 처리용)
        if (callback) {
            callback(payload);
        }
        // 브라우저 알림은 표시하지 않음 - NotificationBell 토스트와 중복 방지
    });
};

// 로컬 알림 표시 (FCM 없이 직접 표시)
export const showLocalNotification = (title, body, data = {}) => {
    if (Notification.permission !== 'granted') return;
    
    const notification = new Notification(title, {
        body,
        icon: '/logo192.png',
        badge: '/logo192.png',
        tag: data.chatId || 'default',
        requireInteraction: true,
        data
    });
    
    notification.onclick = () => {
        window.focus();
        if (data.chatId) {
            window.location.href = `/chat/${data.chatId}`;
        }
        notification.close();
    };
    
    // 알림 소리 (선택사항)
    try {
        const audio = new Audio('/notification-sound.mp3');
        audio.volume = 0.5;
        audio.play().catch(() => {});
    } catch (e) {}
    
    return notification;
};

// 알림 권한 상태 확인
export const getNotificationStatus = () => {
    if (!('Notification' in window)) {
        return 'not_supported';
    }
    return Notification.permission; // 'granted', 'denied', 'default'
};

// 알림 비활성화 및 FCM 토큰 삭제
export const disableNotifications = async (userId) => {
    try {
        if (userId) {
            await updateDoc(doc(db, 'users', userId), {
                fcmToken: deleteField(),
                notificationEnabled: false,
                notificationUpdatedAt: new Date()
            });
        }
        return { success: true };
    } catch (error) {
        console.error('알림 비활성화 실패:', error);
        return { success: false, error: error.message };
    }
};
